import { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { Box, Grid, Typography, CircularProgress,Backdrop, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { format } from 'date-fns';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CommentIcon from '@mui/icons-material/Comment';
import EmailIcon from '@mui/icons-material/Email';
import HelpIcon from '@mui/icons-material/Help';
import BarChartIcon from '@mui/icons-material/BarChart';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import Table from '../../../common/components/Table/Table';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import CommentModal from '../../../common/components/CommentModal';
import CommentWithTable, { DatasetInfo } from '../../../common/components/CommentWithTable';
import ContactModal from '../../../common/components/ContactModal';
import { useFileUpload, useNavItems, usePermission } from '../../../hooks';
import { useUser } from '../../../hooks/useUser';
import { UserRoles } from '../../../common/constants/userConstants';
import { computePlaygroundFilename, isPlaygroundFile, getPlaygroundErrorMessage } from '../../../utils/filenameUtils';
import {
  useENO1Data,
  ENO1File,
  useENO1Download,
  useENO1Finalize,
  useENO1AcceptFile,
  useENO1Reject as useENO1DeleteFile,
  useENO1Comments,
  useENO1SubmitComment,
  ENO1Comment,
  ENO1CommentsResponse,
  TableType,
  StateType,
  SortConfig,
  FilterConfig,
} from './hooks';

import { StyledContainer, MainContent, TableSection } from './eNO1Message.styled';
import { SortDirection, ContextMenuAction } from '../../../common/components/Table/types';
import { getENO1Columns } from './columns/eNO1Columns';

const ENO1Message: FC = () => {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const navItems = useNavItems();
  const { hasPermission, isLoading: permissionLoading } = usePermission('/eno1', '');
  const { data: userData, isLoading: userLoading } = useUser();

  // State to track which tables to display based on filter
  const [tableView, setTableView] = useState<'all' | 'inbound' | 'outbound'>('all');
  // Add state to track status filter
  const [statusView, setStatusView] = useState<'all' | 'pending' | 'completed'>('all');

  // State for filters from URL parameters
  const [yearFilter, setYearFilter] = useState<string | undefined>(undefined);
  const [ppoFilter, setPpoFilter] = useState<string | undefined>(undefined);

  // State for all data table
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirection | undefined>(undefined);
  // Add search state for pending table
  const [searchColumn, setSearchColumn] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  // State for multiple sorts and filters for pending table
  const [pendingSorts, setPendingSorts] = useState<SortConfig[]>([]);
  const [pendingFilters, setPendingFilters] = useState<FilterConfig[]>([]);

  // State for inbound data table
  const [inboundPage, setInboundPage] = useState<number>(0);
  const [inboundPageSize, setInboundPageSize] = useState<number>(10);
  const [inboundSortColumn, setInboundSortColumn] = useState<string | undefined>(undefined);
  const [inboundSortDirection, setInboundSortDirection] = useState<SortDirection | undefined>(
    undefined
  );
  // Add search state for inbound table
  const [inboundSearchColumn, setInboundSearchColumn] = useState<string | undefined>(undefined);
  const [inboundSearchTerm, setInboundSearchTerm] = useState<string | undefined>(undefined);
  // State for multiple sorts and filters for inbound table
  const [inboundSorts, setInboundSorts] = useState<SortConfig[]>([]);
  const [inboundFilters, setInboundFilters] = useState<FilterConfig[]>([]);

  // State for outbound data table
  const [outboundPage, setOutboundPage] = useState<number>(0);
  const [outboundPageSize, setOutboundPageSize] = useState<number>(10);
  const [outboundSortColumn, setOutboundSortColumn] = useState<string | undefined>(undefined);
  const [outboundSortDirection, setOutboundSortDirection] = useState<SortDirection | undefined>(
    undefined
  );
  // Add search state for outbound table
  const [outboundSearchColumn, setOutboundSearchColumn] = useState<string | undefined>(undefined);
  const [outboundSearchTerm, setOutboundSearchTerm] = useState<string | undefined>(undefined);
  // State for multiple sorts and filters for outbound table
  const [outboundSorts, setOutboundSorts] = useState<SortConfig[]>([]);
  const [outboundFilters, setOutboundFilters] = useState<FilterConfig[]>([]);
  const finalizeFile = useENO1Finalize();

  // Add acceptFile hook with success and error handlers
  const { mutateAsync: acceptFile } = useENO1AcceptFile();

  // State for tracking deletion operation type
  const [deletionOperationType, setDeletionOperationType] = useState<string | null>(null);

  // State for comment modal
  const [commentModal, setCommentModal] = useState<{
    isOpen: boolean;
    fileId: number | null;
    isMarkingForRemoval: boolean;
  }>({
    isOpen: false,
    fileId: null,
    isMarkingForRemoval: false,
  });

  // State for CommentWithTable modal
  const [commentWithTableModal, setCommentWithTableModal] = useState<{
    isOpen: boolean;
    selectedFile: ENO1File | null;
    tableData: ENO1Comment[];
  }>({
    isOpen: false,
    selectedFile: null,
    tableData: [],
  });

  // State for Contact modal
  const [contactModal, setContactModal] = useState<{
    isOpen: boolean;
    selectedFile: ENO1File | null;
  }>({
    isOpen: false,
    selectedFile: null,
  });

  // Delete eNO1 file hook
  const deleteFile = useENO1DeleteFile({
    onSuccess: () => {
      let successMessage = 'File deleted successfully';
      
      if (deletionOperationType === 'Mark File for Removal') {
        successMessage = 'File marked for removal successfully';
      } else if (deletionOperationType === 'Confirm Removal') {
        successMessage = 'File removed successfully';
      } else if (deletionOperationType === 'Undo Removal') {
        successMessage = 'Undid removal successfully';
      }
      
      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success',
      });
      
      // Reset operation type
      setDeletionOperationType(null);

      // Reload the page after a short delay to show the updated status
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },
    onError: (error) => {
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : error.message || 'Unknown error';
      setSnackbar({
        open: true,
        message: `${errorMessage}`,
        severity: 'error',
      });
    },
  });

  // Submit comment hook
  const submitComment = useENO1SubmitComment({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Comment submitted successfully',
        severity: 'success',
      });
      handleCloseCommentWithTableModal();
      
      // Refetch table data based on current view
      const refreshTableData = async () => {
        try {
          if (tableView === 'all' || statusView === 'pending') {
            await refetchPendingData();
          }
          if (tableView === 'all' || tableView === 'inbound') {
            await refetchInboundData();
          }
          if (tableView === 'all' || tableView === 'outbound') {
            await refetchOutboundData();
          }
        } catch (error) {
          console.error('Error refreshing table data after comment submission:', error);
        }
      };
      
      refreshTableData();
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Failed to submit comment: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    },
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Add state for confirmation dialogs
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmAction: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmAction: () => {},
  });

  // State for global spinner during copy to playground
  const [copyToPlaygroundStatus, setCopyToPlaygroundStatus] = useState<{
    open: boolean;
    step: string;
  }>({ open: false, step: '' });

  // Get operator PPO from filter or fallback to current user's designatedOperator
  // Memoize to prevent unnecessary recalculations
  const operatorPpo = useMemo(() => {
    // When PPO filter is 'ALL', return undefined to indicate no filtering
    if (ppoFilter === 'ALL') {
      return undefined;
    }
    // Use the PPO filter if it exists, otherwise use current user's designated operator
    return ppoFilter;
  }, [ppoFilter, userData?.designatedOperator]);

  // Fetch pending eNO1 data (table=pending, state=pending)
  const pendingDesignatedOperator = ppoFilter === 'ALL' ? undefined : (ppoFilter);
  const {
    data: pendingData,
    isLoading: isPendingLoading,
    isError: isPendingError,
    refetch: refetchPendingData,
  } = useENO1Data({
    page,
    size: pageSize,
    sorts: pendingSorts.length > 0 ? pendingSorts : undefined,
    filters: pendingFilters.length > 0 ? pendingFilters : undefined,
    sort: sortColumn && sortDirection ? `${sortColumn},${sortDirection}` : undefined, // Legacy support
    table: 'pending' as TableType,
    designatedOperator: pendingDesignatedOperator,
    state: statusView === 'completed' ? 'completed' : statusView === 'pending' ? 'pending' : 'all' as StateType,
    accountingYear: yearFilter,
    searchColumn: searchColumn, // Legacy support
    searchTerm: searchTerm, // Legacy support
    enabled: (tableView === 'all' || statusView === 'pending'),
  });

  // Fetch inbound eNO1 data (table=inbound, state based on statusView)
  const {
    data: inboundData,
    isLoading: isInboundLoading,
    isError: isInboundError,
    refetch: refetchInboundData,
  } = useENO1Data({
    page: inboundPage,
    size: inboundPageSize,
    sorts: inboundSorts.length > 0 ? inboundSorts : undefined,
    filters: inboundFilters.length > 0 ? inboundFilters : undefined,
    sort:
      inboundSortColumn && inboundSortDirection
        ? `${inboundSortColumn},${inboundSortDirection}`
        : undefined, // Legacy support
    table: 'inbound' as TableType,
    designatedOperator: operatorPpo,
    state: statusView === 'completed' ? 'completed' : statusView === 'pending' ? 'pending' : 'all' as StateType,
    accountingYear: yearFilter,
    searchColumn: inboundSearchColumn, // Legacy support
    searchTerm: inboundSearchTerm, // Legacy support
    enabled: (tableView === 'all' || tableView === 'inbound'),
  });

  // Fetch outbound eNO1 data (table=outbound, state based on statusView)
  const {
    data: outboundData,
    isLoading: isOutboundLoading,
    isError: isOutboundError,
    refetch: refetchOutboundData,
  } = useENO1Data({
    page: outboundPage,
    size: outboundPageSize,
    sorts: outboundSorts.length > 0 ? outboundSorts : undefined,
    filters: outboundFilters.length > 0 ? outboundFilters : undefined,
    sort:
      outboundSortColumn && outboundSortDirection
        ? `${outboundSortColumn},${outboundSortDirection}`
        : undefined, // Legacy support
    table: 'outbound' as TableType,
    designatedOperator: operatorPpo,
    state: statusView === 'completed' ? 'completed' : statusView === 'pending' ? 'pending' : 'all' as StateType,
    accountingYear: yearFilter,
    searchColumn: outboundSearchColumn, // Legacy support
    searchTerm: outboundSearchTerm, // Legacy support
    enabled: (tableView === 'all' || tableView === 'outbound'),
  });

  // Fetch comments for the selected file in CommentWithTable modal
  const { data: commentsData, isLoading: isCommentsLoading } = useENO1Comments({
    dataSetId: commentWithTableModal.selectedFile?.fileId || 0,
    enabled: commentWithTableModal.isOpen && !!commentWithTableModal.selectedFile?.fileId
  });

  // Download eNO1 file
  const { mutate: downloadFile, mutateAsync: downloadFileAsync, isPending: isDownloading } = useENO1Download({
    onSuccess: (file) => {
      if (!file) {
        setSnackbar({
          open: true,
          message: 'File downloaded successfully',
          severity: 'success',
        });
      }
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to download file',
        severity: 'error',
      });
    },
  });

  const {
    uploadFileToEndpoint,
  } = useFileUpload();

  // Helper function to check module permissions
  const hasModulePermission = useCallback(
    (permissionName: string) => {
      if (!userData || !userData.modules) return false;

      // Find the eNO1 module
      const eno1Module = userData.modules.find((module) => module.name === 'eNO1 Sampling');

      if (!eno1Module || !eno1Module.permissions) return false;

      // Check if the user has the specific permission and it's enabled
      return eno1Module.permissions.some(
        (permission) => permission.name === permissionName && permission.enabled
      );
    },
    [userData]
  );

  // Helper function to parse multiple sorts from URL
  const parseSortsFromURL = useCallback((params: URLSearchParams, prefix: string): SortConfig[] => {
    const sortParams = params.getAll('sort');
    const parsedSorts: SortConfig[] = [];
    
    // Check for prefixed sort parameters (e.g., pendingSort0, pendingSort1)
    const prefixedSortKeys = Array.from(params.keys()).filter(key => 
      key.startsWith(`${prefix}Sort`) && !key.endsWith('Col') && !key.endsWith('Dir')
    );
    
    if (prefixedSortKeys.length > 0) {
      // Parse prefixed sorts
      prefixedSortKeys.forEach(key => {
        const sortValue = params.get(key);
        if (sortValue) {
          const [column, direction] = sortValue.split(',');
          if (column && (direction === 'ASC' || direction === 'DESC')) {
            parsedSorts.push({ column, direction: direction as 'ASC' | 'DESC' });
          }
        }
      });
    } else {
      // Fallback to legacy single sort format
      const legacyCol = params.get(`${prefix}SortCol`);
      const legacyDir = params.get(`${prefix}SortDir`) as SortDirection | null;
      if (legacyCol && legacyDir) {
        parsedSorts.push({ column: legacyCol, direction: legacyDir });
      }
    }
    
    return parsedSorts;
  }, []);

  // Helper function to update sorts in URL
  const updateSortsInURL = useCallback((sorts: SortConfig[], params: URLSearchParams, prefix: string) => {
    // Clear existing sorts for this prefix
    Array.from(params.keys()).filter(key => key.startsWith(`${prefix}Sort`)).forEach(key => params.delete(key));
    
    // Add new sorts
    sorts.forEach((sort, index) => {
      params.set(`${prefix}Sort${index}`, `${sort.column},${sort.direction}`);
    });
  }, []);

  // Helper function to parse multiple filters from URL
  const parseFiltersFromURL = useCallback((params: URLSearchParams, prefix: string): FilterConfig[] => {
    const filters: FilterConfig[] = [];
    const filterKeys = Array.from(params.keys()).filter(key =>
      key.startsWith(`${prefix}Filter`) && key.endsWith('Col')
    );

    filterKeys.forEach(key => {
      const colSuffix = key.replace(`${prefix}Filter`, '').replace('Col', '');
      const colValue = params.get(key);
      const termKey = `${prefix}Filter${colSuffix}Term`;
      const termValue = params.get(termKey);

      if (colValue && termValue) {
        filters.push({ column: colValue, term: termValue });
      }
    });

    // Also check for legacy single filter format
    const legacyCol = params.get(`${prefix}SearchCol`);
    const legacyTerm = params.get(`${prefix}SearchTerm`);
    if (legacyCol && legacyTerm && !filters.some(f => f.column === legacyCol)) {
      filters.push({ column: legacyCol, term: legacyTerm });
    }

    return filters;
  }, []);

  // Helper function to update filters in URL
  const updateFiltersInURL = useCallback((filters: FilterConfig[], params: URLSearchParams, prefix: string) => {
    // Clear existing filters for this prefix
    Array.from(params.keys()).filter(key => key.startsWith(`${prefix}Filter`) || key.startsWith(`${prefix}Search`)).forEach(key => params.delete(key));

    filters.forEach((filter, index) => {
      params.set(`${prefix}Filter${index}Col`, filter.column);
      params.set(`${prefix}Filter${index}Term`, filter.term);
    });
  }, []);

  // Helper function to update URL parameters while preserving existing ones
  const updateURLParams = useCallback(
    (newParams: Record<string, string | number | null>) => {
      const params = new URLSearchParams(searchParams);

      // Update or add each parameter
      Object.entries(newParams).forEach(([key, value]) => {
        if (value === null) {
          params.delete(key);
        } else {
          params.set(key, String(value));
        }
      });

      // Update the URL
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Function to determine row background color based on inbound/outbound
  const getRowBackgroundColor = useCallback((row: ENO1File): string => {
    // Check if operatorByInboundPpo equals current user's designated operator
    const isInbound = row.operatorByInboundPpo === userData?.designatedOperator;
    
    // Return green for inbound, red for outbound
    return isInbound ? '#e8f5e9' : '#ffebee';
  }, [userData?.designatedOperator]);

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/eno1');
  };

  // Handlers for main table
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    updateURLParams({ pendingPage: newPage });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    // Remove previous page size params to avoid conflicts
    setPageSize(newPageSize);
    setPage(0); // Reset to first page when changing page size

    // Force a refetch by first removing the parameters, then setting them again
    updateURLParams({
      pendingPageSize: null,
      pendingPage: null,
    });

    // Set the new parameters with a slight delay to ensure URL changes are processed
    setTimeout(() => {
      updateURLParams({
        pendingPage: 0,
        pendingPageSize: newPageSize,
      });
    }, 0);
  };

  const handleSort = useCallback(
    (column: string, direction: SortDirection | undefined, searchColumn?: string, searchTerm?: string) => {
      setPage(0);

      // Handle multiple sorts for pending table
      let newSorts: SortConfig[] = [...pendingSorts];
      
      if (direction && direction !== 'undefined' as any) {
        // Check if this column is already in the sorts array
        const existingIndex = newSorts.findIndex((s) => s.column === column);
        
        if (existingIndex >= 0) {
          // If same direction, remove sort; otherwise update direction
          if (newSorts[existingIndex].direction === direction) {
            newSorts.splice(existingIndex, 1);
          } else {
            newSorts[existingIndex].direction = direction;
          }
        } else {
          // Add new sort
          newSorts.push({ column, direction });
        }
      } else {
        // Remove sort for this column if direction is undefined
        newSorts = newSorts.filter((s) => s.column !== column);
      }
      
      setPendingSorts(newSorts);

      // Handle multiple filters for pending table
      let newFilters: FilterConfig[] = [...pendingFilters];
      
      if (searchColumn !== undefined) {
        if (searchTerm && searchTerm.trim()) {
          // Check if this column already has a filter
          const existingFilterIndex = newFilters.findIndex((f) => f.column === searchColumn);
          
          if (existingFilterIndex >= 0) {
            // Update existing filter term
            newFilters[existingFilterIndex].term = searchTerm.trim();
          } else {
            // Add new filter
            newFilters.push({ column: searchColumn, term: searchTerm.trim() });
          }
        } else {
          // Remove filter for this column if searchTerm is empty or undefined
          newFilters = newFilters.filter((f) => f.column !== searchColumn);
        }
      }
      
      setPendingFilters(newFilters);

      // Update legacy state for backward compatibility
      setSortColumn(column);
      setSortDirection(direction);
      setSearchColumn(searchColumn);
      setSearchTerm(searchTerm);
      
      const params = new URLSearchParams(searchParams);
      params.set('pendingPage', '0');
      
      // Update sorts in URL
      updateSortsInURL(newSorts, params, 'pending');
      
      // Update filters in URL
      updateFiltersInURL(newFilters, params, 'pending');
      
      setSearchParams(params);
    },
    [pendingSorts, pendingFilters, searchParams, setSearchParams, updateSortsInURL, updateFiltersInURL]
  );

  // Handlers for inbound table
  const handleInboundPageChange = (newPage: number) => {
    setInboundPage(newPage);
    updateURLParams({ inboundPage: newPage });
  };

  const handleInboundPageSizeChange = (newPageSize: number) => {
    setInboundPageSize(newPageSize);
    setInboundPage(0);

    // Force a refetch by first removing the parameters, then setting them again
    updateURLParams({
      inboundPageSize: null,
      inboundPage: null,
    });

    // Set the new parameters with a slight delay to ensure URL changes are processed
    setTimeout(() => {
      updateURLParams({
        inboundPage: 0,
        inboundPageSize: newPageSize,
      });
    }, 0);
  };

  const handleInboundSort = useCallback(
    (column: string, direction: SortDirection | undefined, searchColumn?: string, searchTerm?: string) => {
      setInboundPage(0);

      // Handle multiple sorts for inbound table
      let newSorts: SortConfig[] = [...inboundSorts];
      
      if (direction && direction !== 'undefined' as any) {
        // Check if this column is already in the sorts array
        const existingIndex = newSorts.findIndex((s) => s.column === column);
        
        if (existingIndex >= 0) {
          // If same direction, remove sort; otherwise update direction
          if (newSorts[existingIndex].direction === direction) {
            newSorts.splice(existingIndex, 1);
          } else {
            newSorts[existingIndex].direction = direction;
          }
        } else {
          // Add new sort
          newSorts.push({ column, direction });
        }
      } else {
        // Remove sort for this column if direction is undefined
        newSorts = newSorts.filter((s) => s.column !== column);
      }
      
      setInboundSorts(newSorts);

      // Handle multiple filters for inbound table
      let newFilters: FilterConfig[] = [...inboundFilters];
      
      if (searchColumn !== undefined) {
        if (searchTerm && searchTerm.trim()) {
          // Check if this column already has a filter
          const existingFilterIndex = newFilters.findIndex((f) => f.column === searchColumn);
          
          if (existingFilterIndex >= 0) {
            // Update existing filter term
            newFilters[existingFilterIndex].term = searchTerm.trim();
          } else {
            // Add new filter
            newFilters.push({ column: searchColumn, term: searchTerm.trim() });
          }
        } else {
          // Remove filter for this column if searchTerm is empty or undefined
          newFilters = newFilters.filter((f) => f.column !== searchColumn);
        }
      }
      
      setInboundFilters(newFilters);

      // Update legacy state for backward compatibility
      setInboundSortColumn(column);
      setInboundSortDirection(direction);
      setInboundSearchColumn(searchColumn);
      setInboundSearchTerm(searchTerm);
      
      const params = new URLSearchParams(searchParams);
      params.set('inboundPage', '0');
      
      // Update sorts in URL
      updateSortsInURL(newSorts, params, 'inbound');
      
      // Update filters in URL
      updateFiltersInURL(newFilters, params, 'inbound');
      
      setSearchParams(params);
    },
    [inboundSorts, inboundFilters, searchParams, setSearchParams, updateSortsInURL, updateFiltersInURL]
  );

  // Handlers for outbound table
  const handleOutboundPageChange = (newPage: number) => {
    setOutboundPage(newPage);
    updateURLParams({ outboundPage: newPage });
  };

  const handleOutboundPageSizeChange = (newPageSize: number) => {
    setOutboundPageSize(newPageSize);
    setOutboundPage(0);

    // Force a refetch by first removing the parameters, then setting them again
    updateURLParams({
      outboundPageSize: null,
      outboundPage: null,
    });

    // Set the new parameters with a slight delay to ensure URL changes are processed
    setTimeout(() => {
      updateURLParams({
        outboundPage: 0,
        outboundPageSize: newPageSize,
      });
    }, 0);
  };

  const handleOutboundSort = useCallback(
    (column: string, direction: SortDirection | undefined, searchColumn?: string, searchTerm?: string) => {
      setOutboundPage(0);

      // Handle multiple sorts for outbound table
      let newSorts: SortConfig[] = [...outboundSorts];
      
      if (direction && direction !== 'undefined' as any) {
        // Check if this column is already in the sorts array
        const existingIndex = newSorts.findIndex((s) => s.column === column);
        
        if (existingIndex >= 0) {
          // If same direction, remove sort; otherwise update direction
          if (newSorts[existingIndex].direction === direction) {
            newSorts.splice(existingIndex, 1);
          } else {
            newSorts[existingIndex].direction = direction;
          }
        } else {
          // Add new sort
          newSorts.push({ column, direction });
        }
      } else {
        // Remove sort for this column if direction is undefined
        newSorts = newSorts.filter((s) => s.column !== column);
      }
      
      setOutboundSorts(newSorts);

      // Handle multiple filters for outbound table
      let newFilters: FilterConfig[] = [...outboundFilters];
      
      if (searchColumn !== undefined) {
        if (searchTerm && searchTerm.trim()) {
          // Check if this column already has a filter
          const existingFilterIndex = newFilters.findIndex((f) => f.column === searchColumn);
          
          if (existingFilterIndex >= 0) {
            // Update existing filter term
            newFilters[existingFilterIndex].term = searchTerm.trim();
          } else {
            // Add new filter
            newFilters.push({ column: searchColumn, term: searchTerm.trim() });
          }
        } else {
          // Remove filter for this column if searchTerm is empty or undefined
          newFilters = newFilters.filter((f) => f.column !== searchColumn);
        }
      }
      
      setOutboundFilters(newFilters);

      // Update legacy state for backward compatibility
      setOutboundSortColumn(column);
      setOutboundSortDirection(direction);
      setOutboundSearchColumn(searchColumn);
      setOutboundSearchTerm(searchTerm);
      
      const params = new URLSearchParams(searchParams);
      params.set('outboundPage', '0');
      
      // Update sorts in URL
      updateSortsInURL(newSorts, params, 'outbound');
      
      // Update filters in URL
      updateFiltersInURL(newFilters, params, 'outbound');
      
      setSearchParams(params);
    },
    [outboundSorts, outboundFilters, searchParams, setSearchParams, updateSortsInURL, updateFiltersInURL]
  );

  // Helper function to validate file operations
  const validateFileOperation = useCallback(
    (row: ENO1File, operation: 'edit' | 'delete' | 'playground') => {
      // Extract relevant data from the row
      const isInboundAccepted = row.inboundAcceptanceDate && row.inboundAcceptanceDate !== '-';
      const isOutboundAccepted = row.outboundAcceptanceDate && row.outboundAcceptanceDate !== '-';
      const isPendingOnUser = row.pendingOn === userData?.designatedOperator;
      const isPendingOnOtherPPO = row.pendingOn && row.pendingOn !== userData?.designatedOperator;
      const isFullyAccepted = isInboundAccepted && isOutboundAccepted;
      const isDraft = !isFullyAccepted && (!row.pendingOn || row.pendingOn === null);
      const isMarkedForRemoval = row.inboundRemovalDate !== null || row.outboundRemovalDate !== null;
      const isFinal = row.fileStatus === 'F';
      const isPlayground = isPlaygroundFile(row);

      // Custom validation based on operation type
      if (operation === 'edit') {
        // First check if the file is marked for removal
        if (isMarkedForRemoval) {
          return {
            allowed: false,
            message: "Sorry you cannot edit this file because it is marked for removal"
          };
        }
        
        // Scenario 1: File is pending on other PPO (not my DO)
        if (isPendingOnOtherPPO) {
          return {
            allowed: false,
            message: "Sorry you can't edit this file, only the other PPO can make amendments."
          };
        }
        
        // Scenario 2: File is fully accepted by both sides
        if (isFullyAccepted) {
          return {
            allowed: false,
            message: "This file has already been agreed on by both PPO's. No further editing is allowed."
          };
        }

        // Scenario 3: File is not pending on anyone (null) and not fully accepted (draft), only allow if it's a playground file
        if (isDraft && !isPlayground) {
          return {
            allowed: false,
            message: "Sorry you can't edit this file, please make sure you selected the highest version."
          };
        }
        
        // Scenario 4: File is pending on the current user (allowed) or it's a playground file (allowed)
        if (isPendingOnUser || isPlayground) {
          return {
            allowed: true,
            message: ""
          };
        }
        
        // Default case
        return {
          allowed: false,
          message: "You don't have permission to edit this file."
        };
      } 
      
      if (operation === 'delete') {
        // Scenario 1: File is fully accepted by both sides
        if (isFullyAccepted) {
          // Check if user is admin
          const isAdmin = userData?.role === UserRoles.ADMIN;
          
          // If user is admin, allow deletion with admin delete confirmation
          if (isAdmin) {
            return {
              allowed: true,
              title: "Admin Delete",
              message: "Are you sure you want to delete this file?",
              requiresComment: false
            };
          }
          // If not admin, show error
          return {
            allowed: false,
            message: "You cannot delete a file which has already been accepted by both DOs."
          };
        }
        
        // Check if file is FINAL
        if (isFinal) {
          // If file is FINAL and already marked for removal
          if (isMarkedForRemoval) {
            // Check if removalDo is populated
            if (row.removalDo) {
              // Compare removalDo with current user's DO
              if (row.removalDo === userData?.designatedOperator) {
                // Same DO - ask if they want to undo removal
                return {
                  allowed: true,
                  title: "Undo Removal",
                  message: "Do you want to undo the removal of this file?",
                  requiresComment: false
                };
              } else {
                // Different DO - ask to confirm removal
                return {
                  allowed: true,
                  title: "Confirm Removal",
                  message: "Do you confirm removal of this file?",
                  requiresComment: false
                };
              }
            }
            // No removalDo but has removal date - standard delete
            return {
              allowed: true,
              title: "Confirm Deletion",
              message: "Are you sure you want to delete this file?",
              requiresComment: false
            };
          }
          // If file is FINAL and not marked for removal, mark for removal with comment
          return {
            allowed: true,
            title: "Mark File for Removal",
            message: "Are you sure you want to mark this file for removal?",
            requiresComment: true
          };
        }
        
        // If file is not FINAL, delete without comment
        return {
          allowed: true,
          title: "Draft File Removal",
          message: "Are you sure you want to delete this file?",
          requiresComment: false
        };
      }
      
      // Default response for unknown operations
      return {
        allowed: false,
        message: "Operation not supported."
      };
    },
    [userData]
  );

  // Handlers for confirmation dialog
  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      isOpen: false,
      title: '',
      message: '',
      confirmAction: () => {},
    });
  };

  const handleConfirmAction = () => {
    // Execute the stored action
    confirmDialog.confirmAction();
    // Close the dialog
    handleCloseConfirmDialog();
  };

  // Handle context menu actions for all tables
  const handleContextMenuAction = useCallback(
    async (action: ContextMenuAction, row: ENO1File, tableType?: 'all' | 'Inbound' | 'Outbound') => {
      switch (action) {
        case 'ViewSamples':
          // Check if user has permission to view samples
          if (hasModulePermission('eNO1 View Samples')) {
            navigate(`/eno1/messages/samples/${row.fileId}`);
          } else {
            setSnackbar({
              open: true,
              message: 'You do not have permission to view samples',
              severity: 'error',
            });
          }
          break;
        case 'editSamples':
          // Check if user has permission to edit samples
          if (hasModulePermission('eNO1 Edit Samples')) {
            // Validate if user can edit this file
            const validation = validateFileOperation(row, 'edit');
            
            if (validation.allowed) {
              navigate(`/eno1/messages/samples/${row.fileId}?editMode=true`);
            } else {
              setSnackbar({
                open: true,
                message: validation.message,
                severity: 'error',
              });
            }
          } else {
            setSnackbar({
              open: true,
              message: 'You do not have permission to edit samples',
              severity: 'error',
            });
          }
          break;
        case 'accept':
          // Check if user has permission to accept files
          if (hasModulePermission('eNO1 Edit Samples')) {
            // Always delegate validation to backend; show its message on error
            try {
              await acceptFile(row.fileId);
              setSnackbar({
                open: true,
                message: 'File has been accepted successfully',
                severity: 'success',
              });
              setTimeout(() => {
                window.location.reload();
              }, 1500);
            } catch (error: any) {
              setSnackbar({
                open: true,
                message: error?.message || 'Failed to accept file',
                severity: 'error',
              });
            }
          } else {
            setSnackbar({
              open: true,
              message: 'You do not have permission to accept files',
              severity: 'error',
            });
          }
          break;
        case 'download':
          // Download the file
          downloadFile({ fileId: row.fileId });
          break;
        case 'delete':
          // Check if user has permission to delete files
          if (hasModulePermission('Delete eNO1 Files')) {
            // Validate if user can delete this file
            const validation = validateFileOperation(row, 'delete') as { allowed: boolean; title?: string; message: string; requiresComment?: boolean };
            
            if (validation.allowed) {
              // Show confirmation dialog
              setConfirmDialog({
                isOpen: true,
                title: validation.title || 'Confirm Deletion',
                message: validation.message,
                confirmAction: () => {
                  // Set the operation type for success message
                  setDeletionOperationType(validation.title || 'Draft File Removal');
                  
                  // If comment is required, open comment modal; otherwise delete directly
                  if (validation.requiresComment) {
                    // Check if this is a FINAL file not marked for removal (marking for removal)
                    const isFinal = row.fileStatus === 'F';
                    const isMarkedForRemoval = row.inboundRemovalDate !== null || row.outboundRemovalDate !== null;
                    handleOpenCommentModal(row.fileId, isFinal && !isMarkedForRemoval);
                  } else {
                    // Delete without comment
                    deleteFile.mutate({
                      fileId: row.fileId,
                      comment: undefined
                    });
                  }
                }
              });
            } else {
              setSnackbar({
                open: true,
                message: validation.message,
                severity: 'error',
              });
            }
          } else {
            setSnackbar({
              open: true,
              message: 'You do not have permission to delete files',
              severity: 'error',
            });
          }
          break;
        case 'comment':
          // Open CommentWithTable modal to display file information and collect comment
          setCommentWithTableModal({
            isOpen: true,
            selectedFile: row,
            tableData: [], // This will be populated by the useENO1Comments hook
          });
          break;
        case 'contact':
          // Open Contact modal to display counterpart emails
          setContactModal({
            isOpen: true,
            selectedFile: row,
          });
          break;
        case 'no2Report':
          // Check if user has permission to view NO2 reports
          if (hasModulePermission('NO2 Report')) {
            // Build query parameters from row data
            const queryParams = new URLSearchParams();
            
            // Set PPO filter based on table type
            let ppoValue = 'All';
            if (tableType === 'Inbound') {
              // For inbound table, use the outbound PPO (who sent the file)
              ppoValue = row.operatorByOutboundPpo || 'All';
            } else if (tableType === 'Outbound') {
              // For outbound table, use the inbound PPO (who receives the file)
              ppoValue = row.operatorByInboundPpo || 'All';
            } else {
              // For 'all' table, try to determine based on available data
              ppoValue = row.operatorByOutboundPpo || row.operatorByInboundPpo || 'All';
            }
            queryParams.set('ppo', ppoValue);
            
            // Set period range based on the row's year and quarter
            if (row.accountingYear && row.accountingQuarter) {
              const fromYear = row.accountingYear;
              const toYear = row.accountingYear;
              const fromQuarter = row.accountingQuarter;
              const toQuarter = row.accountingQuarter;
              // Convert tableType to lowercase to match NO2Report expectations
              const table = (tableType || 'Inbound').toLowerCase();
              queryParams.set('fromYear', fromYear.toString());
              queryParams.set('toYear', toYear.toString());
              queryParams.set('fromQuarter', fromQuarter.toString());
              queryParams.set('toQuarter', toQuarter.toString());
              queryParams.set('table', table);
            }
            
            // Navigate to NO2 report with the constructed query parameters
            navigate(`/eno1/reports/no2?${queryParams.toString()}`);
          } else {
            setSnackbar({
              open: true,
              message: 'You do not have permission to view NO2 reports',
              severity: 'error',
            });
          }
          break;
        case 'no3Report':
          // Check if user has permission to view NO3 reports
          if (hasModulePermission('NO3 Report')) {
            // Build query parameters from row data
            const queryParams = new URLSearchParams();
            
            // Set PPO filter based on table type
            let ppoValue = 'All';
            if (tableType === 'Inbound') {
              // For inbound table, use the outbound PPO (who sent the file)
              ppoValue = row.operatorByOutboundPpo || 'All';
            } else if (tableType === 'Outbound') {
              // For outbound table, use the inbound PPO (who receives the file)
              ppoValue = row.operatorByInboundPpo || 'All';
            } else {
              // For 'all' table, try to determine based on available data
              ppoValue = row.operatorByOutboundPpo || row.operatorByInboundPpo || 'All';
            }
            queryParams.set('ppo', ppoValue);
            
            // Set type parameter (inbound/outbound)
            const type = (tableType || 'Inbound').toLowerCase();
            queryParams.set('type', type);
            
            // Set year parameter from the row's accounting year
            if (row.accountingYear) {
              queryParams.set('year', row.accountingYear.toString());
            }
            
            // Navigate to NO3 report with the constructed query parameters
            navigate(`/eno1/reports/no3?${queryParams.toString()}`);
          } else {
            setSnackbar({
              open: true,
              message: 'You do not have permission to view NO3 reports',
              severity: 'error',
            });
          }
          break;
        case 'copyToPlayground': {
          // Check if user has permission to use the eNO1 Playground
          if (!hasModulePermission('eNO1 Playground')) {
            setSnackbar({
              open: true,
              message: t('eno1.copyToPlayground.errorNoPermission', 'You do not have permission to use the eNO1 Playground'),
              severity: 'error',
            });
            break;
          }

          let playgroundFileName = computePlaygroundFilename(
            row.fileName,
            userData?.designatedOperator ?? ''
          );

          setConfirmDialog({
            isOpen: true,
            title: t('eno1.copyToPlayground.confirmTitle', 'Copy to Playground'),
            message: t(
              'eno1.copyToPlayground.confirmMessage',
              'Do you really want to copy this file to the eNO1 Playground? The filename will be {{filename}}',
              { filename: playgroundFileName },
            ),
            confirmAction: async () => {
              setCopyToPlaygroundStatus({
                open: true,
                step: t('eno1.copyToPlayground.stepDownloading', 'Preparing source…'),
              });
              try {
                const file = await downloadFileAsync({ fileId: row.fileId, getFile: true, forPlayground: true });

                if (!file) {
                  setSnackbar({
                    open: true,
                    message: t(
                      'eno1.copyToPlayground.errorNoFile',
                      'Copy to Playground failed: the source file could not be retrieved (fileId: {{fileId}}).',
                      { fileId: String(row.fileId) }
                    ),
                    severity: 'error',
                  });
                  return;
                }

                setCopyToPlaygroundStatus({
                  open: true,
                  step: t('eno1.copyToPlayground.stepRenaming', 'Preparing playground file…'),
                });

                // Rename the downloaded file to the playground filename without the _draft
                playgroundFileName = computePlaygroundFilename(
                  row.fileName,
                  userData?.designatedOperator ?? '',
                  false
                );
                const renamedFile = new File([file], playgroundFileName, { type: file.type });

                setCopyToPlaygroundStatus({
                  open: true,
                  step: t('eno1.copyToPlayground.stepUploading', 'Uploading to playground…'),
                });

                try {
                  await uploadFileToEndpoint(renamedFile, '/data-set/eno1/record?isFinal=false');

                  setSnackbar({
                    open: true,
                    message: t(
                      'eno1.copyToPlayground.success',
                      'File "{{filename}}" has been successfully copied to the Playground.',
                      { filename: playgroundFileName }
                    ),
                    severity: 'success',
                  });
                } catch (uploadError) {
                  setSnackbar({
                    open: true,
                    message: getPlaygroundErrorMessage(
                      {...(uploadError as Error), fromPlayground: isPlaygroundFile(row)},
                      t,
                      t(
                        'eno1.copyToPlayground.errorUpload',
                        'Copy to Playground failed: an error occurred while uploading "{{filename}}".',
                        { filename: playgroundFileName }
                      ),
                    ),
                    severity: 'error',
                  });
                }
              } catch (error) {
                setSnackbar({
                  open: true,
                  message: getPlaygroundErrorMessage(
                    error,
                    t,
                    t(
                      'eno1.copyToPlayground.errorDownload',
                      'Copy to Playground failed: an error occurred while downloading the file "{{fileName}}".',
                      { fileName: row.fileName }
                    ),
                  ),
                  severity: 'error',
                });
              } finally {
                setCopyToPlaygroundStatus({ open: false, step: '' });
              }
            },
          });
          break;
        }
        // Add other actions as needed
        default:
          break;
      }
    },
    [navigate, downloadFile, downloadFileAsync, uploadFileToEndpoint, hasModulePermission, userData, acceptFile, validateFileOperation, t]
  );

  // Generate column definitions using shared column definitions
  const allColumns = useMemo(() => getENO1Columns({ userData, tableType: 'all' }), [userData]);
  const inboundColumns = useMemo(() => getENO1Columns({ userData, tableType: 'inbound' }), [userData]);
  const outboundColumns = useMemo(() => getENO1Columns({ userData, tableType: 'outbound' }), [userData]);

  const handleRowClick = (row: ENO1File) => {
    // Handle row click - e.g., navigate to detail view
  };

  // Define custom context menu actions for the tables
  const customContextMenuActions = useMemo(() => [
    { 
      action: 'download' as ContextMenuAction, 
      label: 'Download', 
      icon: <CloudDownloadIcon /> 
    },
    { 
      action: 'accept' as ContextMenuAction, 
      label: 'Accept', 
      icon: <CheckIcon /> 
    },
    { 
      action: 'delete' as ContextMenuAction, 
      label: 'Delete', 
      icon: <DeleteIcon /> 
    },
    { 
      action: 'ViewSamples' as ContextMenuAction, 
      label: 'View Samples', 
      icon: <VisibilityIcon /> 
    },
    { 
      action: 'editSamples' as ContextMenuAction, 
      label: 'Edit Samples', 
      icon: <EditIcon /> 
    },
    { 
      action: 'no2Report' as ContextMenuAction, 
      label: 'NO2 Report', 
      icon: <BarChartIcon /> 
    },
    { 
      action: 'no3Report' as ContextMenuAction, 
      label: 'NO3 Report', 
      icon: <BarChartIcon /> 
    },
    { 
      action: 'comment' as ContextMenuAction, 
      label: 'Comment', 
      icon: <CommentIcon /> 
    },
    { 
      action: 'contact' as ContextMenuAction, 
      label: 'Contact', 
      icon: <EmailIcon /> 
    },
    { 
      action: 'more' as ContextMenuAction, 
      label: 'More', 
      icon: <HelpIcon /> 
    },
    { 
      action: 'copyToPlayground' as ContextMenuAction, 
      label: t('eno1.contextMenu.copyToPlayground', 'Copy to Playground'), 
      icon: <ContentCopyIcon /> 
    },
  ], [t]);

  // Helper function to get the current table view from URL
  const getTableViewFromUrl = useCallback(() => {
    return (searchParams.get('type') as 'all' | 'inbound' | 'outbound') || 'all';
  }, [searchParams]);

  // Update tableView, filters, and pagination based on URL parameter changes
  useEffect(() => {
    const typeFilter = searchParams.get('type') || 'all';
    setTableView(typeFilter as 'all' | 'inbound' | 'outbound');

    // Extract state filter from search params (changed from 'status' to 'state')
    const stateFilter = searchParams.get('state') || 'all';
    setStatusView(stateFilter as 'all' | 'pending' | 'completed');

    // Extract year filter from search params
    const year = searchParams.get('year');
    // If year is not provided, undefined, or specifically "all", set it appropriately
    if (!year || year === 'all') {
      setYearFilter(undefined); // When year is "all" or not provided, use undefined to fetch all years
    } else {
      setYearFilter(year);
    }

    // Extract PPO filter from search params
    const ppo = searchParams.get('ppo');
    // If PPO is not provided, undefined, or specifically "ALL", set it appropriately
    if (!ppo || ppo === 'ALL') {
      setPpoFilter(undefined); // When PPO is "ALL" or not provided, use undefined to fetch all PPOs
    } else {
      setPpoFilter(ppo);
    }

    // Get pagination and sorting parameters for main table (pending actions)
    const pendingPageParam = searchParams.get('pendingPage');
    const pendingPageSizeParam = searchParams.get('pendingPageSize');
    const pendingSortColParam = searchParams.get('pendingSortCol');
    const pendingSortDirParam = searchParams.get('pendingSortDir') as SortDirection | null;
    const pendingSearchColParam = searchParams.get('pendingSearchCol');
    const pendingSearchTermParam = searchParams.get('pendingSearchTerm');

    // Get pagination and sorting parameters for inbound table
    const inboundPageParam = searchParams.get('inboundPage');
    const inboundPageSizeParam = searchParams.get('inboundPageSize');
    const inboundSortColParam = searchParams.get('inboundSortCol');
    const inboundSortDirParam = searchParams.get('inboundSortDir') as SortDirection | null;
    const inboundSearchColParam = searchParams.get('inboundSearchCol');
    const inboundSearchTermParam = searchParams.get('inboundSearchTerm');

    // Get pagination and sorting parameters for outbound table
    const outboundPageParam = searchParams.get('outboundPage');
    const outboundPageSizeParam = searchParams.get('outboundPageSize');
    const outboundSortColParam = searchParams.get('outboundSortCol');
    const outboundSortDirParam = searchParams.get('outboundSortDir') as SortDirection | null;
    const outboundSearchColParam = searchParams.get('outboundSearchCol');
    const outboundSearchTermParam = searchParams.get('outboundSearchTerm');

    // Parse multiple sorts and filters from URL
    const parsedPendingSorts = parseSortsFromURL(searchParams, 'pending');
    const parsedPendingFilters = parseFiltersFromURL(searchParams, 'pending');
    const parsedInboundSorts = parseSortsFromURL(searchParams, 'inbound');
    const parsedInboundFilters = parseFiltersFromURL(searchParams, 'inbound');
    const parsedOutboundSorts = parseSortsFromURL(searchParams, 'outbound');
    const parsedOutboundFilters = parseFiltersFromURL(searchParams, 'outbound');

    // Update main table state
    if (pendingPageParam !== null) setPage(parseInt(pendingPageParam, 10));
    if (pendingPageSizeParam !== null) setPageSize(parseInt(pendingPageSizeParam, 10));
    if (pendingSortColParam !== null) setSortColumn(pendingSortColParam);
    if (pendingSortDirParam !== null) setSortDirection(pendingSortDirParam);
    if (pendingSearchColParam !== null) setSearchColumn(pendingSearchColParam);
    if (pendingSearchTermParam !== null) setSearchTerm(pendingSearchTermParam);

    // Update pending sorts and filters
    if (JSON.stringify(parsedPendingSorts) !== JSON.stringify(pendingSorts)) {
      setPendingSorts(parsedPendingSorts);
    }
    if (JSON.stringify(parsedPendingFilters) !== JSON.stringify(pendingFilters)) {
      setPendingFilters(parsedPendingFilters);
    }

    // Update inbound table state
    if (inboundPageParam !== null) setInboundPage(parseInt(inboundPageParam, 10));
    if (inboundPageSizeParam !== null) setInboundPageSize(parseInt(inboundPageSizeParam, 10));
    if (inboundSortColParam !== null) setInboundSortColumn(inboundSortColParam);
    if (inboundSortDirParam !== null) setInboundSortDirection(inboundSortDirParam);
    if (inboundSearchColParam !== null) setInboundSearchColumn(inboundSearchColParam);
    if (inboundSearchTermParam !== null) setInboundSearchTerm(inboundSearchTermParam);

    // Update inbound sorts and filters
    if (JSON.stringify(parsedInboundSorts) !== JSON.stringify(inboundSorts)) {
      setInboundSorts(parsedInboundSorts);
    }
    if (JSON.stringify(parsedInboundFilters) !== JSON.stringify(inboundFilters)) {
      setInboundFilters(parsedInboundFilters);
    }

    // Update outbound table state
    if (outboundPageParam !== null) setOutboundPage(parseInt(outboundPageParam, 10));
    if (outboundPageSizeParam !== null) setOutboundPageSize(parseInt(outboundPageSizeParam, 10));
    if (outboundSortColParam !== null) setOutboundSortColumn(outboundSortColParam);
    if (outboundSortDirParam !== null) setOutboundSortDirection(outboundSortDirParam);
    if (outboundSearchColParam !== null) setOutboundSearchColumn(outboundSearchColParam);
    if (outboundSearchTermParam !== null) setOutboundSearchTerm(outboundSearchTermParam);

    // Update outbound sorts and filters
    if (JSON.stringify(parsedOutboundSorts) !== JSON.stringify(outboundSorts)) {
      setOutboundSorts(parsedOutboundSorts);
    }
    if (JSON.stringify(parsedOutboundFilters) !== JSON.stringify(outboundFilters)) {
      setOutboundFilters(parsedOutboundFilters);
    }

    // Reset pagination when filters change for any table view that becomes visible
    if (tableView !== getTableViewFromUrl()) {
      // Reset all pagination to initial state when table view changes
      setPage(0);
      setInboundPage(0);
      setOutboundPage(0);

      // Update URL to reflect the pagination reset
      const resetParams = new URLSearchParams(searchParams);
      resetParams.delete('pendingPage');
      resetParams.delete('inboundPage');
      resetParams.delete('outboundPage');
      setSearchParams(resetParams);
    }
  }, [searchParams, getTableViewFromUrl, setSearchParams, parseSortsFromURL, parseFiltersFromURL, pendingSorts, pendingFilters, inboundSorts, inboundFilters, outboundSorts, outboundFilters]);

  // Show loading indicator based on which tables are visible
  const showLoading =
    ((statusView === 'all' && tableView === 'all' || statusView === 'pending') && isPendingLoading) ||
    ((tableView === 'all' || tableView === 'inbound') && isInboundLoading) ||
    ((tableView === 'all' || tableView === 'outbound') && isOutboundLoading) ||
    permissionLoading ||
    userLoading;

  // Show error based on which tables are visible
  const showError =
    ((statusView === 'all' && tableView === 'all' || statusView === 'pending') && isPendingError) ||
    ((tableView === 'all' || tableView === 'inbound') && isInboundError) ||
    ((tableView === 'all' || tableView === 'outbound') && isOutboundError);

  // Comment modal handlers
  const handleOpenCommentModal = (fileId: number, isMarkingForRemoval: boolean = false) => {
    setCommentModal({
      isOpen: true,
      fileId,
      isMarkingForRemoval,
    });
  };

  const handleCloseCommentModal = () => {
    setCommentModal({
      isOpen: false,
      fileId: null,
      isMarkingForRemoval: false,
    });
  };

  const handleCommentSubmit = (comment: string) => {
    if (commentModal.fileId) {
      // Call delete API with file ID and comment (comment may be empty for non-FINAL files)
      deleteFile.mutate({
        fileId: commentModal.fileId,
        comment: comment.trim() || undefined,
      });

      // Close the modal
      handleCloseCommentModal();
    }
  };

  // CommentWithTable modal handlers
  const handleCloseCommentWithTableModal = () => {
    setCommentWithTableModal({
      isOpen: false,
      selectedFile: null,
      tableData: [],
    });
  };

  const handleCommentWithTableSubmit = (comment: string) => {
    if (!commentWithTableModal.selectedFile) return;
    
    submitComment.mutate({
      dataSetId: commentWithTableModal.selectedFile.fileId,
      comment
    });
  };

  // Effect to refresh all data when the component mounts or when dependent variables change
  useEffect(() => {
    // Refresh all data when the component mounts
    const refreshAllData = async () => {
      try {
        // Refresh the data based on current view
        if (tableView === 'all' || statusView === 'pending') {
          await refetchPendingData();
        }

        if ((tableView === 'all' || tableView === 'inbound') && 
            (statusView === 'all' || statusView === 'completed')) {
          await refetchInboundData();
        }

        if ((tableView === 'all' || tableView === 'outbound') && 
            (statusView === 'all' || statusView === 'completed')) {
          await refetchOutboundData();
        }
      } catch (error) {
        console.error('Error refreshing data:', error);
      }
    };

    refreshAllData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.designatedOperator, statusView, tableView]); // Re-run when user's designatedOperator, statusView or tableView changes

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='eno1-message-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='eNO1 Message'
            subtitle='eNO1 Sampling'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            showFilters={{ state: true, type: true, year: true, ppo: true }}
            data-testid="section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="eno1-message-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='eno1-message-main-content'>
            {showLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="loading-indicator">
                <CircularProgress />
              </Box>
            ) : showError ? (
              <Alert severity='error' data-testid="error-alert">Error loading data</Alert>
            ) : !hasPermission ? (
              <Alert severity='warning' data-testid="permission-error-alert">You don't have permission to view this section</Alert>
            ) : (
              <StyledContainer data-testid="tables-container">
                {/* All eNO1 Messages Table - Show only when:
                    1. statusView is 'all' AND tableView is 'all', OR
                    2. statusView is 'pending' (regardless of tableView) */}
                {((statusView === 'all' && tableView === 'all') || statusView === 'pending') && (
                  <TableSection data-testid="pending-actions-section">
                    <Table
                      data={pendingData?.content || []}
                      columns={allColumns}
                      page={page}
                      pageSize={pageSize}
                      totalCount={pendingData?.totalElements}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      todoColumn={true}
                      moduleName='Pending Actions'
                      rowEventHandlers={{
                        onClick: handleRowClick,
                        onContextMenuAction: handleContextMenuAction,
                      }}
                      rowBackgroundColor={getRowBackgroundColor}
                      onSort={handleSort}
                      defaultSort={pendingSorts.length > 0 ? pendingSorts[0] : (sortColumn && sortDirection ? { column: sortColumn, direction: sortDirection } : undefined)}
                      defaultSorts={pendingSorts.length > 0 ? pendingSorts : undefined}
                      defaultFilters={pendingFilters.length > 0 ? pendingFilters : undefined}
                      initialSearchColumn={searchColumn}
                      initialSearchTerm={searchTerm}
                      customActions={customContextMenuActions}
                      hideDefaultActions={true}
                      tableType="all"
                      data-testid="pending-actions-table"
                    />
                  </TableSection>
                )}

                {/* Inbound eNO1 Messages Table - Show when tableView includes inbound */}
                {(tableView === 'all' || tableView === 'inbound') && (
                  <TableSection data-testid="inbound-section">
                    {isInboundLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="inbound-loading-indicator">
                        <CircularProgress />
                      </Box>
                    ) : isInboundError ? (
                      <Alert severity='error' data-testid="inbound-error-alert">Error loading inbound data</Alert>
                    ) : (
                      <Table
                        data={inboundData?.content || []}
                        columns={inboundColumns}
                        page={inboundPage}
                        pageSize={inboundPageSize}
                        totalCount={inboundData?.totalElements}
                        onPageChange={handleInboundPageChange}
                        onPageSizeChange={handleInboundPageSizeChange}
                        rowBackgroundColor="rgb(230, 245, 230)"
                        todoColumn={true}
                        moduleName={`Inbound from ${ppoFilter && ppoFilter !== 'ALL' ? ppoFilter : '*'} to ${userData?.designatedOperator || ''}`}
                        rowEventHandlers={{
                          onClick: handleRowClick,
                          onContextMenuAction: handleContextMenuAction,
                        }}
                        onSort={handleInboundSort}
                        defaultSort={inboundSorts.length > 0 ? inboundSorts[0] : (inboundSortColumn && inboundSortDirection ? { column: inboundSortColumn, direction: inboundSortDirection } : undefined)}
                        defaultSorts={inboundSorts.length > 0 ? inboundSorts : undefined}
                        defaultFilters={inboundFilters.length > 0 ? inboundFilters : undefined}
                        initialSearchColumn={inboundSearchColumn}
                        initialSearchTerm={inboundSearchTerm}
                        customActions={customContextMenuActions}
                        hideDefaultActions={true}
                        tableType="Inbound"
                        data-testid="inbound-table"
                      />
                    )}
                  </TableSection>
                )}

                {/* Outbound eNO1 Messages Table - Show when tableView includes outbound */}
                {(tableView === 'all' || tableView === 'outbound') && (
                  <TableSection data-testid="outbound-section">
                    {isOutboundLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }} data-testid="outbound-loading-indicator">
                        <CircularProgress />
                      </Box>
                    ) : isOutboundError ? (
                      <Alert severity='error' data-testid="outbound-error-alert">Error loading outbound data</Alert>
                    ) : (
                      <Table
                        data={outboundData?.content || []}
                        columns={outboundColumns}
                        page={outboundPage}
                        pageSize={outboundPageSize}
                        totalCount={outboundData?.totalElements}
                        onPageChange={handleOutboundPageChange}
                        onPageSizeChange={handleOutboundPageSizeChange}
                        rowBackgroundColor="rgb(255, 240, 240)"
                        todoColumn={true}
                        moduleName={`Outbound from ${userData?.designatedOperator || ''} to ${ppoFilter && ppoFilter !== 'ALL' ? ppoFilter : '*'}`}
                        rowEventHandlers={{
                          onClick: handleRowClick,
                          onContextMenuAction: handleContextMenuAction,
                        }}
                        onSort={handleOutboundSort}
                        defaultSort={outboundSorts.length > 0 ? outboundSorts[0] : (outboundSortColumn && outboundSortDirection ? { column: outboundSortColumn, direction: outboundSortDirection } : undefined)}
                        defaultSorts={outboundSorts.length > 0 ? outboundSorts : undefined}
                        defaultFilters={outboundFilters.length > 0 ? outboundFilters : undefined}
                        initialSearchColumn={outboundSearchColumn}
                        initialSearchTerm={outboundSearchTerm}
                        customActions={customContextMenuActions}
                        tableType="Outbound"
                        data-testid="outbound-table"
                      />
                    )}
                  </TableSection>
                )}
              </StyledContainer>
            )}

            {/* Notification Snackbar */}
            <NotificationSnackbar
              open={snackbar.open}
              message={snackbar.message}
              severity={snackbar.severity}
              onClose={handleSnackbarClose}
              data-testid="eno1-message-notification"
            />
          </MainContent>
        </Grid>
      </Grid>

      {/* Comment Modal for Deletion */}
      <CommentModal
        isOpen={commentModal.isOpen}
        onClose={handleCloseCommentModal}
        onSubmit={handleCommentSubmit}
        title={commentModal.isMarkingForRemoval ? 'Mark File for Removal' : 'Delete eNO1 File'}
        description={commentModal.isMarkingForRemoval 
          ? 'Please provide a comment explaining why you are marking this file for removal.'
          : 'Please provide a comment explaining why you are deleting this file.'}
        submitText={commentModal.isMarkingForRemoval ? 'Mark for Removal' : 'Delete'}
        cancelText='Cancel'
        isLoading={deleteFile.isPending}
        data-testid="delete-comment-modal"
      />

      {/* CommentWithTable Modal */}
      <CommentWithTable
        isOpen={commentWithTableModal.isOpen}
        onClose={handleCloseCommentWithTableModal}
        onSubmit={handleCommentWithTableSubmit}
        title={`Comment on File ${commentWithTableModal.selectedFile?.fileId || ''}`}
        submitText='Submit Comment'
        cancelText='Cancel'
        tableData={commentsData?.comments || []}
        datasetInfo={commentsData ? {
          accountingYear: commentsData.accountingYear,
          accountingQuarter: commentsData.accountingQuarter,
          inboundRemovalDate: commentsData.inboundRemovalDate,
          outboundRemovalDate: commentsData.outboundRemovalDate,
        } : undefined}
        columns={[
          {
            Header: 'Date',
            accessor: 'date',
            width: '20%',
          },
          {
            Header: 'DO',
            accessor: 'ppo',
            width: '20%',
          },
          {
            Header: 'Version',
            accessor: 'version',
            width: '20%',
          },
          {
            Header: 'Comment',
            accessor: 'comment',
            width: '39%',
          }
        ]}
        commentRequired={true}
        commentLabel='Your Comment'
        commentPlaceholder='Enter your comment here...'
        maxWidth="lg"
        fullWidth={true}
        isLoading={submitComment.isPending}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.isOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        data-testid="confirmation-dialog"
      >
        <DialogTitle id="alert-dialog-title">{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary" data-testid="dialog-cancel-button">
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} color="primary" autoFocus data-testid="dialog-confirm-button">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact Modal */}
      <ContactModal
        isOpen={contactModal.isOpen}
        onClose={() => setContactModal({ isOpen: false, selectedFile: null })}
        row={contactModal.selectedFile}
        moduleName="eNO1 Sampling"
      />

      {/* Global spinner for copy to playground */}
      <Backdrop
        open={copyToPlaygroundStatus.open}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 1, color: '#fff', flexDirection: 'column', gap: 2 }}
        data-testid="copy-to-playground-spinner"
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" color="inherit">
          {t('eno1.copyToPlayground.spinnerTitle', 'Copying to Playground')}
        </Typography>
        {copyToPlaygroundStatus.step && (
          <Typography variant="body2" color="inherit">
            {copyToPlaygroundStatus.step}
          </Typography>
        )}
      </Backdrop>
    </Box>
  );
};

export default ENO1Message;
